// Sticky add to cart offer (mobile):
// - rotates the messages from theme settings
// - shows "Best Price" = variant price minus the discount of the highest tier reached by
//   (cart total + this product). Below the first tier only the price is shown.

// Minimal Shopify money formatter (window.theme.moneyFormat, e.g. "₹{{amount}}"); prices are rounded to whole units
function formatMoney(cents) {
  const format = (window.theme && window.theme.moneyFormat) || '₹{{amount}}';
  const whole = Math.round(cents / 100);
  const withCommas = (value, separator) => value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, separator);

  return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, placeholder) => {
    switch (placeholder) {
      case 'amount_with_comma_separator':
      case 'amount_no_decimals_with_comma_separator':
        return withCommas(whole, '.');
      case 'amount_with_apostrophe_separator':
        return withCommas(whole, "'");
      default:
        return withCommas(whole, ',');
    }
  });
}

/*
  Cart change detection.
  The theme announces add-to-cart as a DOM event ("cart:item-added" / "cart:updated" on document), but
  removing an item or changing a quantity (quick cart, cart page) only goes through theme.js's private
  event bus, which this file can't listen to. So we watch the cart requests themselves: after any
  successful /cart/add, /cart/change, /cart/update or /cart/clear (fetch or XHR, from the theme or an
  app) a "sticky-offer:cart-changed" event is fired and every offer re-reads the cart total.
  Requests are passed through untouched.
*/
const CART_MUTATION_URL = /\/cart\/(add|change|update|clear)(\.js)?(\?|#|$)/;
const CART_CHANGED_EVENT = 'sticky-offer:cart-changed';

function notifyCartChanged() {
  document.dispatchEvent(new CustomEvent(CART_CHANGED_EVENT));
}

function requestUrl(input) {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.href;
  return (input && input.url) || '';
}

if (!window.__stickyOfferCartWatch) {
  window.__stickyOfferCartWatch = true;

  const originalFetch = window.fetch;
  window.fetch = function (input, init) {
    const response = originalFetch.apply(this, arguments);
    if (CART_MUTATION_URL.test(requestUrl(input))) {
      response.then((res) => res.ok && notifyCartChanged()).catch(() => {});
    }
    return response;
  };

  const originalOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    if (CART_MUTATION_URL.test(String(url))) {
      this.addEventListener('load', () => {
        if (this.status >= 200 && this.status < 300) notifyCartChanged();
      });
    }
    return originalOpen.apply(this, arguments);
  };
}

// Cart value used for the tiers, same as the cart progress bar (sections/quick-cart.liquid):
// pre-discount line prices (original_line_price), milestone gift lines (_mmx_gift_milestone) skipped.
// Using cart.total_price (after discounts) made "You're ₹X away" disagree with the bar.
function cartValue(cart) {
  if (!cart || !Array.isArray(cart.items)) return 0;
  return cart.items.reduce((total, item) => {
    if (item.properties && item.properties._mmx_gift_milestone) return total;
    return total + (item.original_line_price || 0);
  }, 0);
}

// One /cart.js request shared by all offers on the page (sticky bar, header pill, New Price pill)
let cartRequest = null;
function getCart() {
  if (!cartRequest) {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    cartRequest = fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })
      .then((response) => response.json())
      .finally(() => {
        cartRequest = null;
      });
  }
  return cartRequest;
}

class StickyAtcOffer extends HTMLElement {
  connectedCallback() {
    this.price = parseInt(this.dataset.variantPrice, 10) || 0;
    this.cartTotal = 0;
    this.tiers = this.parseTiers();

    this.pricesEl = this.querySelector('[data-offer-prices]');
    this.priceEl = this.querySelector('[data-offer-price]');
    this.bestEl = this.querySelector('[data-offer-best]');
    this.bestPriceEl = this.querySelector('[data-offer-best-price]');

    this.onCartUpdated = (event) => {
      const cart = event.detail && event.detail.cart;
      if (cart) {
        this.cartTotal = cartValue(cart);
        this.render();
      } else {
        this.fetchCart();
      }
    };

    this.onVariantChange = (event) => {
      // Product cards keep their own price; the event is the PDP's selected variant
      if (this.hasAttribute('data-ignore-variant-change')) return;

      const variant = event.detail && event.detail.variant;
      if (!variant) return;
      this.price = variant.price;
      this.render();
    };

    // Remove / quantity change from quick cart or cart page (see cart change detection above)
    this.onCartChanged = () => this.fetchCart();

    document.addEventListener('cart:updated', this.onCartUpdated);
    document.addEventListener('cart:item-added', this.onCartUpdated);
    document.addEventListener(CART_CHANGED_EVENT, this.onCartChanged);
    document.addEventListener('product:variant-change', this.onVariantChange);

    this.render();
    this.fetchCart();
    this.startMessages();
  }

  disconnectedCallback() {
    document.removeEventListener('cart:updated', this.onCartUpdated);
    document.removeEventListener('cart:item-added', this.onCartUpdated);
    document.removeEventListener(CART_CHANGED_EVENT, this.onCartChanged);
    document.removeEventListener('product:variant-change', this.onVariantChange);
    clearInterval(this.messageTimer);
  }

  parseTiers() {
    try {
      return JSON.parse(this.dataset.tiers || '[]')
        .filter((tier) => tier.amount > 0 && tier.percent > 0)
        .sort((a, b) => a.amount - b.amount);
    } catch (error) {
      return [];
    }
  }

  fetchCart() {
    getCart()
      .then((cart) => {
        this.cartTotal = cartValue(cart);
        this.render();
      })
      .catch(() => {});
  }

  // Highest tier reached by the cart total once this product is added.
  // Below the first threshold, the first tier's discount is still used, so a Best Price always shows
  // when tiers are set (tiers are sorted by amount, so tiers[0] is the first tier).
  currentPercent() {
    if (!this.tiers.length) return 0;

    const projectedTotal = this.cartTotal + this.price;
    let percent = this.tiers[0].percent;
    this.tiers.forEach((tier) => {
      if (projectedTotal >= tier.amount) percent = tier.percent;
    });
    return percent;
  }

  // Next tier not reached yet by the current cart total (this product is NOT counted), or null when all are reached
  nextTier() {
    return this.tiers.find((tier) => this.cartTotal < tier.amount) || null;
  }

  // "You're ₹X Away from Y% OFF" based on the cart total only (template from theme settings: [amount], [percent])
  renderTierMessage() {
    const el = this.querySelector('[data-offer-tier-message]');
    if (!el) return;

    const tier = this.nextTier();
    const template = this.dataset.tierMessage || '';
    el.textContent = tier && template
      ? template
          .replace(/\[amount\]/g, formatMoney(tier.amount - this.cartTotal))
          .replace(/\[percent\]/g, tier.percent)
      : '';

    this.syncMessages();
  }

  // Price row (price + Best Price) only shows when a tier applies;
  // with no tier reached, or no tiers set, the whole row is hidden.
  render() {
    this.renderTierMessage();

    if (!this.pricesEl) return;

    // No Best Price pill (product outside the "Activate progress only for collection" collection):
    // the row only shows the actual price
    if (!this.bestEl && this.priceEl) {
      this.priceEl.textContent = formatMoney(this.price);
      this.pricesEl.hidden = !this.price;
      return;
    }

    // priceEl is optional: the inline version (Product header / New Price) only shows the Best Price pill
    if (!this.bestEl || !this.bestPriceEl) return;

    const percent = this.currentPercent();
    if (!percent || !this.price) {
      this.pricesEl.hidden = true;
      return;
    }

    if (this.priceEl) this.priceEl.textContent = formatMoney(this.price);
    this.bestPriceEl.textContent = formatMoney(this.price * (1 - percent / 100));
    this.bestEl.hidden = false;
    this.pricesEl.hidden = false;
  }

  // Messages that can be shown right now: all theme-setting messages, plus the tier message when it has text
  availableMessages() {
    return [...this.querySelectorAll('.sticky-offer__message')].filter(
      (el) => !el.hasAttribute('data-offer-tier-message') || el.textContent.trim() !== ''
    );
  }

  // Keep exactly one available message active (e.g. the tier message just became empty, or it is the only one)
  syncMessages() {
    const messages = [...this.querySelectorAll('.sticky-offer__message')];
    const available = this.availableMessages();
    const active = messages.find((el) => el.classList.contains('is-active'));

    if (active && available.includes(active)) return;
    messages.forEach((el) => el.classList.remove('is-active'));
    if (available[0]) available[0].classList.add('is-active');
  }

  // Rotation: message 1 → 2 → 3 → tier message → 1 …  (an empty tier message is skipped)
  startMessages() {
    if (!this.querySelector('.sticky-offer__message')) return;

    const speed = parseInt(this.dataset.speed, 10) || 3000;

    this.messageTimer = setInterval(() => {
      const available = this.availableMessages();
      if (available.length < 2) {
        this.syncMessages();
        return;
      }

      const current = available.findIndex((el) => el.classList.contains('is-active'));
      const next = available[(current + 1) % available.length];
      available.forEach((el) => el.classList.remove('is-active'));
      next.classList.add('is-active');
    }, speed);
  }
}

if (!customElements.get('sticky-atc-offer')) {
  customElements.define('sticky-atc-offer', StickyAtcOffer);
}
